import { useState, useEffect } from "react";
import { Outlet, NavLink, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  LayoutDashboard, Users, LifeBuoy, LogOut, Shield, TrendingUp,
  FileStack, Megaphone, Terminal, UserPlus, Menu, ChevronDown, Award,
} from "lucide-react";
import { dashboardPathForUser } from "@/lib/authRedirect";
import api from "@/lib/api";
import { AccountSwitcherSection } from "@/components/AccountSwitcher";
import { toast } from "sonner";
import { getSavedAccountsCount } from "@/lib/authAccounts";

const adminNavGroups = [
  {
    title: "Vue d'ensemble",
    items: [
      { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    ],
  },
  {
    title: "Utilisateurs",
    items: [
      { path: "/admin/users", label: "Utilisateurs", icon: Users },
      { path: "/admin/badges", label: "Badges", icon: Award },
      { path: "/admin/invitations", label: "Invitations admin", icon: UserPlus },
    ],
  },
  {
    title: "Opérations",
    items: [
      { path: "/admin/support", label: "Support", icon: LifeBuoy, badgeKey: "support" },
      { path: "/admin/revenue", label: "Revenus", icon: TrendingUp },
      { path: "/admin/content", label: "Contenu", icon: FileStack },
    ],
  },
  {
    title: "Communications",
    items: [
      { path: "/admin/communications", label: "Annonces & emails", icon: Megaphone },
    ],
  },
  {
    title: "Système",
    items: [
      { path: "/admin/system", label: "Terminal & maintenance", icon: Terminal },
    ],
  },
];

function SidebarNav({ badges, onClose }) {
  return (
    <nav className="flex-1 overflow-y-auto px-3 py-2">
      {adminNavGroups.map((group, gi) => (
        <div key={group.title} className={gi > 0 ? "mt-5" : ""}>
          <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            {group.title}
          </p>
          <div className="space-y-0.5">
            {group.items.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-600 text-white"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <link.icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{link.label}</span>
                {link.badgeKey && badges[link.badgeKey] > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                    {badges[link.badgeKey] > 99 ? "99+" : badges[link.badgeKey]}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [openTickets, setOpenTickets] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);

  const refreshBadge = () => {
    api.get("/admin/support?status=open")
      .then((r) => setOpenTickets(r.data.length))
      .catch(() => {});
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    refreshBadge();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (user.role !== "admin") return <Navigate to={`/${user.role}/dashboard`} />;

  const hasMultipleAccounts = getSavedAccountsCount() > 1;

  const handleLogout = async ({ all = false } = {}) => {
    const result = await logout({ all });
    if (result?.switchedTo) {
      toast.success(`Connecté en tant que ${result.switchedTo.name}`);
      window.location.replace(dashboardPathForUser(result.switchedTo));
      return;
    }
    navigate("/login", { replace: true });
  };

  const badges = { support: openTickets };

  const userMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2 h-10 pl-1.5 pr-3 border-slate-200 hover:bg-slate-50"
          data-testid="admin-user-menu-btn"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.picture} />
            <AvatarFallback className="bg-blue-600 text-white text-xs font-bold">
              {user.name?.[0]?.toUpperCase() ?? "A"}
            </AvatarFallback>
          </Avatar>
          <div className="hidden sm:block text-left min-w-0 max-w-[140px]">
            <p className="text-sm font-medium text-slate-900 truncate leading-tight">{user.name}</p>
            <p className="text-[11px] text-slate-500 truncate leading-tight">Admin</p>
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <div className="px-3 py-2 border-b border-slate-100 mb-1">
          <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
          <p className="text-xs text-slate-500 truncate">{user.email}</p>
        </div>
        <AccountSwitcherSection
          onLogoutCurrent={() => handleLogout({ all: false })}
          onLogoutAll={hasMultipleAccounts ? () => handleLogout({ all: true }) : undefined}
        />
        {!hasMultipleAccounts && (
          <DropdownMenuItem onClick={() => handleLogout()} className="text-red-600 cursor-pointer" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <aside className="hidden md:flex w-64 flex-shrink-0 bg-slate-900 border-r border-slate-800 flex-col overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-heading text-sm font-bold text-white uppercase tracking-widest">COACH.</p>
              <p className="text-xs text-slate-400">Centre de contrôle</p>
            </div>
          </div>
        </div>
        <SidebarNav badges={badges} />
        <div className="px-3 py-4 border-t border-slate-800 shrink-0">
          <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            Mon compte
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-700 bg-slate-800/60 hover:bg-slate-800 transition-colors"
                data-testid="admin-sidebar-account-btn"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user.picture} />
                  <AvatarFallback className="bg-blue-600 text-white text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() ?? "A"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.name}</p>
                  <p className="text-xs text-slate-400 truncate">Comptes & déconnexion</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-72">
              <AccountSwitcherSection
                onLogoutCurrent={() => handleLogout({ all: false })}
                onLogoutAll={hasMultipleAccounts ? () => handleLogout({ all: true }) : undefined}
              />
              {!hasMultipleAccounts && (
                <DropdownMenuItem onClick={() => handleLogout()} className="text-red-600 cursor-pointer" data-testid="admin-sidebar-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-slate-900 border-slate-800 flex flex-col">
          <div className="px-6 py-5 border-b border-slate-800 shrink-0">
            <p className="font-heading text-sm font-bold text-white uppercase tracking-widest">COACH.</p>
            <p className="text-xs text-slate-400">Centre de contrôle</p>
          </div>
          <SidebarNav badges={badges} onClose={() => setSheetOpen(false)} />
          <div className="mt-auto px-3 py-4 border-t border-slate-800 shrink-0 space-y-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-700 bg-slate-800/60 hover:bg-slate-800 transition-colors"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-blue-600 text-white text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() ?? "A"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-white truncate">{user.name}</p>
                    <p className="text-xs text-slate-400 truncate">Comptes & déconnexion</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-72">
                <AccountSwitcherSection />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between gap-3 px-4 md:px-8 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="md:hidden shrink-0" onClick={() => setSheetOpen(true)} data-testid="admin-mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">Administration</p>
              <p className="text-xs text-slate-500 truncate hidden sm:block">MyCoaching — centre de contrôle</p>
            </div>
          </div>
          {userMenu}
        </header>

        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="p-4 md:p-8 max-w-7xl mx-auto">
            <Outlet context={{ refreshBadge }} />
          </div>
        </main>
      </div>
    </div>
  );
}